import { useMemo, useState } from 'react';
import Page from '../../../../components/PageWrapper';
import useModal from '../../../../hooks/modalHook';
import GTable from '../../../../components/elements/GTable';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createNewCountry, getAllOperationalCountries } from '../../../../api/requests';
import { PlusIcon } from '@heroicons/react/24/solid';
import Button from '../../../../components/form-control/Button';
import { Formik } from 'formik';
import * as Yup from 'yup'
import SelectDropdown, { ISelectDropdownItem } from '../../../../components/form-control/SelectDropdown';
import Loader from '../../../../components/Loader';
import useCountries from '../../../../hooks/countryHook';
import { showToast } from '../../../../utils/miscFunctions';

interface CountriesInOperationProps {
    isSettings?: boolean;
}

const CountriesInOperation = ({isSettings= false}: CountriesInOperationProps) => {

    const { ModalComponent, updateModalStates, closeModal } = useModal();
    const queryClient = useQueryClient()
    const {addCountry, CountriesModal, loading, countries} = useCountries();

    const columns = useMemo(() => [
        {
            Header: 'M',
            columns: [
                {
                    Header: 'Name',
                    accessor: 'name',
                    Cell: (row: any) => {
                        return (
                            <div className="flex items-center gap-2">
                                <img src={row.row.original.flag} alt="country flag" className='h-10 w-10 rounded-full' />
                                <span>{row.cell.value ? row.cell.value : '-'}</span>
                            </div>
                        )
                    }
                },
                {
                    Header: 'ISO',
                    accessor: 'iso',
                    Cell: (row: any) => {
                        return <span>{row.cell.value ? row.cell.value : '-'}</span>;
                    }
                },
                {
                    Header: 'Code',
                    accessor: 'code',
                    Cell: (row: any) => {
                        return <span>{row.cell.value ? row.cell.value : '-'}</span>;
                    }
                },
                {
                    Header: 'Currency',
                    accessor: 'currencyIso',
                    Cell: (row: any) => {
                        return <span>{row.cell.value ? row.cell.value : '-'}</span>;
                    }
                },
                {
                    Header: 'Currency Symbol',
                    accessor: 'currencySymbol',
                    Cell: (row: any) => {
                        return <span>{row.cell.value ? row.cell.value : '-'}</span>;
                    }
                },
            ]
        }
    ],
        []
    );

  

   

    return (
        <Page title="Countries">
            <div className='p-5'>
             {loading ? (
            
                    <Loader message="Fetching countries. Please wait..." brollyLoaderFixed  />
               )
                :
            (<div className="min-h-full bg-white  dark:bg-darkBg w-full ">
                {!isSettings && (<div className="flex items-center justify-between  ">
                    
                    <div className="">
                        <Button
                            icon={<PlusIcon width="20px" height="20px" className="mr-2" />}
                            className="p-2 h-[40px] rounded"
                            bgColor="primary"
                            label="Add Country"
                            onClick={addCountry}
                        />
                    </div>
                </div>)}

                <div className="overflow-x-auto  mt-4">
                    <GTable
                        columns={columns}
                        data={countries}
                        tableType={'expense'}
                        loading={loading}
                        isRowLinked={false}
                        showSearch={false}
                    />
                </div>
                <ModalComponent />
                <CountriesModal />

            </div>)}
            </div>
        </Page>
    );
};

interface ICreateCountryProp {
    onProceed: () => void
    onClose: () => void
}



export const CreateCountry = ({ onProceed, onClose }: ICreateCountryProp) => {
    const [submitting, setSubmitting] = useState(false);
    const [allCountries, setAllCountries] = useState([])

    const getCountriesList = async () => {
        try {
            const response = await fetch('https://restcountries.com/v3.1/all');
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const countries = await response.json();
            setAllCountries(countries)
        } catch (error) {
            console.error('There has been a problem with your fetch operation:', error);
        }
    };

    useQuery({
        queryKey: ['getCountriesList',],
        queryFn: getCountriesList
    })

    return (
        <Formik
            initialValues={{
                name: "",
            }}
            validateOnMount={false}
            validationSchema={
                Yup.object().shape({
                    name: Yup.string().required('Country name is required'),
                })}
            onSubmit={async (values, { resetForm }) => {
                try {
                    setSubmitting(true);
                    const response: any = await createNewCountry(values.name)

                    if (response) {
                        showToast.success(response.message ?? 'Country created successfully');
                        onProceed();
                    }
                } catch (error: any) {
                    showToast.error(error.description);
                } finally {
                    setSubmitting(false);
                }

            }}>
            {({ values, errors, touched, handleSubmit, handleChange, setFieldValue, handleBlur }) => (
                <form className="p-5" onSubmit={handleSubmit}>
                    <SelectDropdown
                        onChange={(option: string | null) => setFieldValue('name', option)}
                        onBlur={handleBlur}
                        listSelectedValue={values.name}
                        list={allCountries.map((country: any) => ({
                            name: country.name?.common,
                            value: country.name?.common
                        } as ISelectDropdownItem))}
                        placeholder='Select country'
                        searchPlaceHolder='type country name'
                        search
                        sort
                        name="name"
                        label="Country"
                        required
                        error={touched.name ? errors.name : undefined}
                        helperText={touched.name ? errors.name : undefined}
                    />

                    <div className="w-full flex gap-4 mt-6 !mb-4">
                        <Button
                            type="button"
                            label="Cancel"
                            onClick={onClose}
                            bgColor='secondary'
                  fullWidth
                            loading={submitting}
                            disabled={submitting}
                        />
                        <Button
                            type="submit"
                            label="Save"
                            bgColor='primary'
              fullWidth
                            loading={submitting}
                            disabled={submitting}
                        />
                    </div>
                </form>
            )}
        </Formik >
    );
};

export default CountriesInOperation;
